import { Injectable, NgZone } from '@angular/core';
import { Feature, FeatureCollection } from 'geojson';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import { AlertSettings, LatLng, SelectedArea } from 'src/app/hickory-api';
import { BaseHref } from '../../base-href.helper';
import {
  BrandHeatmapClusterRenderer,
  POLYGON_COLORS,
  addFeatureCollectionToMap,
  centerAndFitBounds,
} from '../helpers/maps-helpers';
import { calculateFeatureCollectionCenter } from '../../model/utility';

export interface LayerOptions {
  showPolygonLayers?: boolean;
  showFacilityMarkers?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardLayersService {
  private polygonLayers: google.maps.Data[] = [];
  private markerElements: google.maps.marker.AdvancedMarkerElement[] = [];
  private markerClusterer: MarkerClusterer | null = null;

  // Marker and icon pools for reuse
  private readonly markerPool: google.maps.marker.AdvancedMarkerElement[] = [];
  private readonly iconCache = new Map<string, HTMLImageElement>();
  private readonly activeMarkers = new Set<google.maps.marker.AdvancedMarkerElement>();

  constructor(private readonly ngZone: NgZone) {}

  /**
   * Removes all the polygon layers and markers from the map
   */
  resetAllLayers(): void {
    this.resetPolygonLayers();
    this.resetMarkers();
    this.markerPool.length = 0;
    this.iconCache.clear();
    this.activeMarkers.clear();
  }

  /**
   * Removes all the polygon layers from the map
   */
  resetPolygonLayers(): void {
    this.polygonLayers.forEach((layer) => {
      layer?.unbindAll();
      layer?.setMap(null);
    });
    this.polygonLayers = [];
  }

  /**
   * Removes all the markers from the map, including the clusterer
   * Returns markers to pool for reuse instead of destroying them
   */
  resetMarkers(): void {
    if (this.markerClusterer) {
      google.maps.event.clearInstanceListeners(this.markerClusterer);
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }

    // Return active markers to pool instead of destroying
    this.activeMarkers.forEach((marker) => {
      this.returnMarkerToPool(marker);
    });

    this.activeMarkers.clear();
    this.markerElements = [];
  }

  /**
   * Returns a marker to the pool for reuse
   */
  private returnMarkerToPool(marker: google.maps.marker.AdvancedMarkerElement): void {
    if (marker) {
      // Clear event listeners and reset properties
      google.maps.event.clearInstanceListeners(marker);
      marker.map = null;
      marker.position = null;
      marker.title = '';

      // Return to pool if not already there
      if (!this.markerPool.includes(marker)) {
        this.markerPool.push(marker);
      }
    }
  }

  /**
   * Gets a marker from the pool or creates a new one
   */
  private getMarkerFromPool(): google.maps.marker.AdvancedMarkerElement {
    if (this.markerPool.length > 0) {
      return this.markerPool.pop();
    }

    // Create new marker if pool is empty
    return new google.maps.marker.AdvancedMarkerElement();
  }

  populatePolygonLayers(
    map: google.maps.Map,
    alerts: AlertSettings[],
    options: LayerOptions = {},
    onPolygonClick?: (alert: AlertSettings) => void,
  ): void {
    if (!options.showPolygonLayers) return;

    this.resetPolygonLayers();

    for (const [idx, alert] of alerts.entries()) {
      const areas = alert.location?.areas;
      if (!areas?.length) continue;

      for (const area of areas) {
        if (!area.geofences) continue;

        try {
          const data = addFeatureCollectionToMap(area.geofences as FeatureCollection, map, {
            strokeColor: POLYGON_COLORS[idx % POLYGON_COLORS.length],
            strokeOpacity: 0.5,
            strokeWeight: 1,
            fillColor: POLYGON_COLORS[idx % POLYGON_COLORS.length],
            fillOpacity: 0.1,
          });

          if (onPolygonClick) {
            // Google event listeners are not patched by Zone.js, so we need to run outside Angular zone
            // otherwise there is a delay in the event being triggered
            this.ngZone.runOutsideAngular(() => {
              data.addListener('click', () => {
                requestAnimationFrame(() => {
                  this.ngZone.run(() => {
                    onPolygonClick(alert);
                  });
                });
              });
            });
          }

          this.polygonLayers.push(data);
        } catch (error) {
          console.error('Error adding polygon layer:', error);
        }
      }
    }
  }

  populateAllLayers(
    map: google.maps.Map,
    alerts: AlertSettings[],
    options: LayerOptions = {},
    onPolygonClick?: (alert: AlertSettings) => void,
    onFacilityMarkerClick?: (facility: object) => void,
  ): void {
    if (!map) return;

    this.resetAllLayers();

    this.populatePolygonLayers(map, alerts, options, onPolygonClick);

    if (options.showFacilityMarkers) {
      this.renderMarkersToMap(map, alerts, onFacilityMarkerClick);
    }
  }

  /**
   * Process a single alert and render it's layers to the map based on the provided options
   */
  processAlertLayers(
    alert: AlertSettings,
    map: google.maps.Map,
    options: LayerOptions = {},
    onPolygonClick?: (alert: AlertSettings) => void,
    onFacilityMarkerClick?: (facility: object) => void,
  ): { center: LatLng | null } {
    const hasPoint = alert?.location?.point;
    const hasAreas = alert?.location?.areas?.length;

    if (!hasPoint && !hasAreas) {
      return { center: null };
    }

    const center = hasPoint ? alert.location.point : this.getAreasCenter(alert.location?.areas || []);

    this.populateAllLayers(map, [alert], options, onPolygonClick, onFacilityMarkerClick);
    this.fitMapToLayers(map, [alert]);

    return { center };
  }

  /**
   * Render markers directly to the map with clustering using pooled markers
   */
  private renderMarkersToMap(
    map: google.maps.Map,
    alerts: AlertSettings[],
    onMarkerClick?: (facility: object) => void,
  ): void {
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];

    for (const alert of alerts) {
      const facilities = alert.location?.facilities || [];

      for (const facility of facilities) {
        if (!facility.location) return;
        if (this.isInvalidCoordinate(facility.location.latitude, facility.location.longitude)) return;

        try {
          const markerElement = this.getMarkerFromPool();

          markerElement.position = { lat: facility.location.latitude, lng: facility.location.longitude };
          markerElement.title = facility.name;
          markerElement.content = this.createMarkerIcon(
            `${BaseHref.get()}resources/images/facility_marker.svg`,
            40,
            40,
            'Alert facility marker',
          );

          if (onMarkerClick) {
            // Google event listeners are not patched by Zone.js, so we need to run outside Angular zone
            // otherwise there is a delay in the event being triggered
            this.ngZone.runOutsideAngular(() => {
              markerElement.addListener('click', () => {
                requestAnimationFrame(() => {
                  this.ngZone.run(() => {
                    onMarkerClick(facility);
                  });
                });
              });
            });
          }

          markers.push(markerElement);
          this.activeMarkers.add(markerElement);
        } catch (error) {
          console.error(`Failed to create marker for facility ${facility.id}:`, error);
        }
      }
    }

    this.markerElements = markers;

    if (markers.length > 0) {
      this.markerClusterer = new MarkerClusterer({
        markers,
        map,
        renderer: new BrandHeatmapClusterRenderer(),
      });
    }
  }

  /**
   * Validates coordinates to prevent invalid markers
   */
  private isInvalidCoordinate(lat: number, lng: number): boolean {
    return isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0);
  }

  fitMapToLayers(map: google.maps.Map, alerts: AlertSettings[]): void {
    const bounds = new google.maps.LatLngBounds();

    // Add polygon bounds
    alerts.forEach((alert) => {
      alert.location?.areas?.forEach((area) => {
        if (area.geofences) {
          const featureCollection = area.geofences as FeatureCollection;
          featureCollection.features.forEach((feature) => {
            this.extendPolygonBounds(bounds, feature);
          });
        }
      });
    });

    // Add marker bounds
    this.markerElements.forEach((marker) => {
      if (marker.position) {
        const position = marker.position as google.maps.LatLng;
        bounds.extend(position);
      }
    });

    centerAndFitBounds(
      map,
      bounds,
      this.markerElements.map((marker) => ({ lat: marker.position.lat as number, lng: marker.position.lng as number })),
      this.polygonLayers,
    );
  }

  private extendPolygonBounds(bounds: google.maps.LatLngBounds, polygon: Feature): void {
    if (polygon.geometry.type === 'Polygon') {
      polygon.geometry.coordinates[0].forEach((point) => {
        bounds.extend(new google.maps.LatLng(point[1], point[0]));
      });
    }

    if (polygon.geometry.type === 'MultiPolygon') {
      polygon.geometry.coordinates.forEach((ring) => {
        ring.forEach((points) => {
          points.forEach((point) => {
            bounds.extend(new google.maps.LatLng(point[1], point[0]));
          });
        });
      });
    }
  }

  /**
   * Infers the center by calculating the center of the first area's geofences. This
   * the "center" of the polygon is merely based on the bounding box, so it may not be
   * completely accurate.
   */
  getAreasCenter(areas: SelectedArea[]): LatLng | null {
    if (!areas?.length) return null;
    return calculateFeatureCollectionCenter(areas[0]?.geofences);
  }

  /**
   * Creates or retrieves cached marker icon
   */
  createMarkerIcon(src: string, height: number, width: number, alt: string): HTMLImageElement {
    const cacheKey = `${src}-${height}-${width}-${alt}`;

    if (this.iconCache.has(cacheKey)) {
      // Clone the cached icon to avoid reference issues
      const cachedIcon = this.iconCache.get(cacheKey);
      return cachedIcon.cloneNode(true) as HTMLImageElement;
    }

    // Create new icon and cache it
    const icon = Object.assign(document.createElement('img'), { src, height, width, alt });
    this.iconCache.set(cacheKey, icon);

    // Return a clone to avoid reference issues
    return icon.cloneNode(true) as HTMLImageElement;
  }

  getPolygonLayers(): google.maps.Data[] {
    return this.polygonLayers;
  }

  getMarkerElements(): google.maps.marker.AdvancedMarkerElement[] {
    return this.markerElements;
  }

  getMarkerClusterer(): MarkerClusterer | null {
    return this.markerClusterer;
  }

  addMarkersToCluster(markers: google.maps.marker.AdvancedMarkerElement[]): void {
    if (this.markerClusterer) {
      this.markerClusterer.addMarkers(markers);
      this.markerElements.push(...markers);
      markers.forEach((marker) => this.activeMarkers.add(marker));
    }
  }

  removeMarkersFromCluster(markers: google.maps.marker.AdvancedMarkerElement[]): void {
    if (this.markerClusterer) {
      this.markerClusterer.removeMarkers(markers);
      this.markerElements = this.markerElements.filter((marker) => !markers.includes(marker));

      // Return removed markers to pool
      markers.forEach((marker) => {
        this.activeMarkers.delete(marker);
        this.returnMarkerToPool(marker);
      });
    }
  }

  updateClusterConfig(map: google.maps.Map, options: { maxDistance?: number; gridSize?: number }): void {
    if (this.markerClusterer && this.markerElements.length > 0) {
      // Recreate clusterer with new options
      this.markerClusterer.clearMarkers();

      this.markerClusterer = new MarkerClusterer({
        markers: this.markerElements,
        map,
        renderer: new BrandHeatmapClusterRenderer(),
        algorithm: new GridAlgorithm({
          maxDistance: options.maxDistance || 60000,
          gridSize: options.gridSize || 60,
        }),
      });
    }
  }

  zoomToFitAllAlerts(map: google.maps.Map, alerts: AlertSettings[]): void {
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    alerts.forEach((alert) => {
      if (alert.location?.point) {
        bounds.extend(new google.maps.LatLng(alert.location.point.lat, alert.location.point.lng));
        hasPoints = true;
      }
    });

    if (hasPoints && !bounds.isEmpty()) {
      map.fitBounds(bounds);
      // Add some padding
      setTimeout(() => {
        if (map.getZoom() > 15) map.setZoom(15);
      }, 100);
    }
  }

  resetMapView(
    map: google.maps.Map,
    defaultCenter: { lat: number; lng: number } = { lat: 39.8283, lng: -98.5795 },
  ): void {
    map.setCenter(defaultCenter);
    map.setZoom(4);
  }

  createPriorityMarkerIcon(priority: string): HTMLImageElement {
    const iconMap = {
      Emergency: '/resources/images/marker_red.svg',
      NonEmergency: '/resources/images/marker_blue.svg',
      default: '/resources/images/location_marker.svg',
    };

    const iconUrl = iconMap[priority] ?? iconMap['default'];
    return this.createMarkerIcon(iconUrl, 36, 36, `${priority} alert marker`);
  }
}
